import { useState } from 'react';
import './styles.css';
import Icons from '../Icon/icons';
import { useAccessibility } from '../../shared/context/a11y-context';
import { useTranslation } from '../../shared/i18n/i18n-context';

interface AccessibilityControlsProps {
  onFontSizeChange: (level: number) => void;
}

const AccessibilityControls = ({ onFontSizeChange }: AccessibilityControlsProps) => {
  const { screenReaderEnabled, toggleScreenReader, announceMessage } = useAccessibility();
  const { t } = useTranslation('accessibility');
  const [fontSizeLevel, setFontSizeLevel] = useState(0);

  const increaseFontSize = () => {
    const newLevel = Math.min(fontSizeLevel + 1, 4);
    setFontSizeLevel(newLevel);
    onFontSizeChange(newLevel);
    announceMessage(t('fontIncreased').replace('{level}', String(newLevel)), 'polite');
  };

  const decreaseFontSize = () => {
    const newLevel = Math.max(fontSizeLevel - 1, 0);
    setFontSizeLevel(newLevel);
    onFontSizeChange(newLevel);
    announceMessage(t('fontDecreased').replace('{level}', String(newLevel)), 'polite');
  };

  const handleToggleScreenReader = () => {
    toggleScreenReader();
  };

  return (
    <div className="accessibility-controls">
      <button
        onClick={handleToggleScreenReader}
        className={`screen-reader-btn ${screenReaderEnabled ? 'active' : ''}`}
        title={screenReaderEnabled ? t('disableScreenReader') : t('enableScreenReader')}
        aria-label={screenReaderEnabled ? t('disableScreenReader') : t('enableScreenReader')}
        aria-pressed={screenReaderEnabled}
      >
        <Icons 
          iconName={screenReaderEnabled ? "RecordVoiceOver" : "VoiceOverOff"} 
          addTooltip={screenReaderEnabled ? t('screenReaderEnabled') : t('screenReaderDisabled')}
          color={screenReaderEnabled ? "#3a8d8e" : "#666"} 
          fontSize={24} 
        />
      </button>

      <div
        className="font-size-group"
        aria-label={t('fontSizeControls')}
      >
        <button
          onClick={decreaseFontSize}
          className="font-size-btn"
          title={t('decreaseFontTitle')}
          disabled={fontSizeLevel === 0}
          aria-label={t('decreaseFontSize')}
        >
          A-
        </button>

        <span className="font-size-divider" aria-hidden="true" />

        <button
          onClick={increaseFontSize}
          className="font-size-btn"
          title={t('increaseFontTitle')}
          disabled={fontSizeLevel === 4}
          aria-label={t('increaseFontSize')}
        >
          A+
        </button>
      </div>
    </div>
  );
};

export default AccessibilityControls;
